import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { I18nextProvider } from 'react-i18next'
import type { MotionValue } from 'framer-motion'
import LyricStage from '../../vendor/folia-src/host/LyricStage'
import LyricStagePlayerChrome from '../../vendor/folia-src/host/chrome/LyricStagePlayerChrome'
import {
  DEFAULT_CAPPELLA_TUNING,
  DEFAULT_CLASSIC_TUNING,
  DEFAULT_CLADDAGH_TUNING,
  DEFAULT_DIORAMA_TUNING,
  DEFAULT_FUME_TUNING,
  DEFAULT_LATENT_BACKGROUND_TUNING,
  DEFAULT_MONET_BACKGROUND_TUNING,
  DEFAULT_MONET_TUNING,
  DEFAULT_NOMAND_BACKGROUND_TUNING,
  DEFAULT_PARTITA_TUNING,
  DEFAULT_PENDOLO_TUNING,
  DEFAULT_SONNET_TUNING,
  DEFAULT_TEMPERA_TUNING,
  DEFAULT_TILT_TUNING,
  type AudioBands,
  type DualTheme,
  type LyricData,
  type Theme,
  type UrlBackgroundItem,
  type VisualizerBackgroundConfig,
  type VisualizerBackgroundMode,
  type VisualizerMode,
} from '../../vendor/folia-src/types'
import type { VisualizerTuningBundle } from '../../vendor/folia-src/components/visualizer/tuningRegistry'
import { DEFAULT_VISUALIZER_MODE } from '../../vendor/folia-src/types/visualizerModes'
import { BASE_DUAL_THEME, selectTheme } from '../../vendor/folia-src/theme'
import type { VisualizerBackgroundActions } from '../../vendor/folia-src/components/visualizer/backgrounds/definition'
import type { LyricStageRecordStatus } from '../../vendor/folia-src/host/chrome/LyricStagePlayerChrome'
import i18n from '../bridge/i18n'
import { JizuraSettingsPanel } from '../jizura/JizuraSettings'
import { JizuraPalettePicker, JizuraVersionRow } from '../jizura/JizuraAppearance'

const STORAGE_KEY = 'musey-lyric-stage'
const JIZURA_MODE_BADGES = { 'mod:musey:jizura': 'NEW' }

const createDefaultBackground = (): VisualizerBackgroundConfig => ({
  mode: 'common',
  common: {
    useCoverColorBg: true,
    opacity: 0.75,
    disableGeometricBackground: false,
    disableVignette: false,
  },
  latent: { tuning: { ...DEFAULT_LATENT_BACKGROUND_TUNING } },
  monet: { tuning: { ...DEFAULT_MONET_BACKGROUND_TUNING } },
  nomand: { tuning: { ...DEFAULT_NOMAND_BACKGROUND_TUNING } },
})

const createDefaultTunings = (): VisualizerTuningBundle => ({
  classic: DEFAULT_CLASSIC_TUNING,
  partita: DEFAULT_PARTITA_TUNING,
  fume: DEFAULT_FUME_TUNING,
  claddagh: DEFAULT_CLADDAGH_TUNING,
  cappella: DEFAULT_CAPPELLA_TUNING,
  tilt: DEFAULT_TILT_TUNING,
  diorama: DEFAULT_DIORAMA_TUNING,
  monet: DEFAULT_MONET_TUNING,
  pendolo: DEFAULT_PENDOLO_TUNING,
  sonnet: DEFAULT_SONNET_TUNING,
  tempera: DEFAULT_TEMPERA_TUNING,
})

const readStored = (): {
  mode?: VisualizerMode
  background?: VisualizerBackgroundConfig
  tunings?: VisualizerTuningBundle
  isDaylight?: boolean
  dualTheme?: DualTheme
} => {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) as ReturnType<typeof readStored> : {}
  } catch {
    return {}
  }
}

export interface LyricStageIslandProps {
  lyrics: LyricData
  coverUrl?: string | null
  songTitle?: string | null
  songArtist?: string | null
  duration: number
  paused: boolean
  currentTime: MotionValue<number>
  audioPower: MotionValue<number>
  audioBands: AudioBands
  tracks?: Array<{ id: string; title: string }>
  currentTrackId?: string | null
  onTogglePlay: () => void
  onSeek: (time: number) => void
  onSelectTrack?: (id: string) => void
  onPrevTrack?: () => void
  onNextTrack?: () => void
  onExitLyricStage: () => void
  chromeHidden?: boolean
  recordSupported?: boolean
  recordStatus?: LyricStageRecordStatus | null
  /** iframe 宿主录制时关掉舞台内 HUD，改由父页展示 */
  showRecordHud?: boolean
  onStageSurfaceReady?: (el: HTMLElement | null) => void
  onStartPvRecord?: () => void
  onCancelPvRecord?: () => void
  panelFooter?: React.ReactNode
}

const LyricStageIslandInner: React.FC<LyricStageIslandProps> = ({
  lyrics,
  coverUrl,
  songTitle,
  songArtist,
  duration,
  paused,
  currentTime,
  audioPower,
  audioBands,
  tracks = [],
  currentTrackId = null,
  onTogglePlay,
  onSeek,
  onSelectTrack,
  onPrevTrack,
  onNextTrack,
  onExitLyricStage,
  chromeHidden = false,
  recordSupported = false,
  recordStatus = null,
  showRecordHud = true,
  onStageSurfaceReady,
  onStartPvRecord,
  onCancelPvRecord,
  panelFooter,
}) => {
  const stored = useMemo(() => readStored(), [])
  const recordingBusy = Boolean(
    recordStatus
    && (recordStatus.phase === 'preparing'
      || recordStatus.phase === 'countdown'
      || recordStatus.phase === 'recording'
      || recordStatus.phase === 'stopping'),
  )
  const [dualTheme, setDualTheme] = useState<DualTheme>(() => ({
    light: { ...BASE_DUAL_THEME.light, ...(stored.dualTheme?.light || {}) },
    dark: { ...BASE_DUAL_THEME.dark, ...(stored.dualTheme?.dark || {}) },
  }))
  const [isDaylight, setIsDaylight] = useState(
    typeof stored.isDaylight === 'boolean' ? stored.isDaylight : true,
  )
  const [mode, setMode] = useState<VisualizerMode>(stored.mode || DEFAULT_VISUALIZER_MODE)
  const [background, setBackground] = useState<VisualizerBackgroundConfig>(() => ({
    ...createDefaultBackground(),
    ...(stored.background || {}),
    common: {
      ...createDefaultBackground().common,
      ...(stored.background?.common || {}),
    },
  }))
  const [tunings, setTunings] = useState<VisualizerTuningBundle>(() => ({
    ...createDefaultTunings(),
    ...(stored.tunings || {}),
  }))

  const effectiveIsDaylight = mode === 'mod:musey:jizura' ? false : isDaylight
  const theme = useMemo(
    () => selectTheme(dualTheme, { isDaylight: effectiveIsDaylight }),
    [dualTheme, effectiveIsDaylight],
  )

  const persist = useCallback((next: {
    mode: VisualizerMode
    background: VisualizerBackgroundConfig
    tunings: VisualizerTuningBundle
    isDaylight: boolean
    dualTheme: DualTheme
  }) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
    } catch {
      /* ignore */
    }
  }, [])

  useEffect(() => {
    persist({ mode, background, tunings, isDaylight, dualTheme })
  }, [mode, background, tunings, isDaylight, dualTheme, persist])

  const patchTuning = <K extends keyof VisualizerTuningBundle>(
    key: K,
    patch: Partial<NonNullable<VisualizerTuningBundle[K]>>,
  ) => {
    setTunings(prev => ({
      ...prev,
      [key]: { ...(prev[key] as object), ...patch },
    }))
  }

  const onThemeChange = (next: Theme) => {
    setDualTheme(prev => (
      isDaylight ? { ...prev, light: next } : { ...prev, dark: next }
    ))
  }

  const backgroundActions = useMemo<VisualizerBackgroundActions>(() => ({
    onModeChange: (next: VisualizerBackgroundMode) => {
      setBackground(prev => ({ ...prev, mode: next }))
    },
    common: {
      onCoverColorChange: (enabled: boolean) => {
        setBackground(prev => ({
          ...prev,
          common: { ...prev.common, useCoverColorBg: enabled },
        }))
      },
      onOpacityChange: (opacity: number) => {
        setBackground(prev => ({
          ...prev,
          common: { ...prev.common, opacity },
        }))
      },
      onDisableGeometricChange: (disabled: boolean) => {
        setBackground(prev => ({
          ...prev,
          common: { ...prev.common, disableGeometricBackground: disabled },
        }))
      },
      onDisableVignetteChange: (disabled: boolean) => {
        setBackground(prev => ({
          ...prev,
          common: { ...prev.common, disableVignette: disabled },
        }))
      },
    },
    latent: {
      onTuningChange: (patch) => {
        setBackground(prev => ({
          ...prev,
          latent: {
            ...prev.latent,
            tuning: {
              ...(prev.latent?.tuning ?? DEFAULT_LATENT_BACKGROUND_TUNING),
              ...patch,
            },
          },
        }))
      },
      onResetTuning: () => {
        setBackground(prev => ({
          ...prev,
          latent: { ...prev.latent, tuning: { ...DEFAULT_LATENT_BACKGROUND_TUNING } },
        }))
      },
    },
    monet: {
      onTuningChange: (patch) => {
        setBackground(prev => ({
          ...prev,
          monet: {
            ...prev.monet,
            tuning: {
              ...(prev.monet?.tuning ?? DEFAULT_MONET_BACKGROUND_TUNING),
              ...patch,
            },
          },
        }))
      },
      onResetTuning: () => {
        setBackground(prev => ({
          ...prev,
          monet: { ...prev.monet, tuning: { ...DEFAULT_MONET_BACKGROUND_TUNING } },
        }))
      },
    },
    nomand: {
      onTuningChange: (patch) => {
        setBackground(prev => ({
          ...prev,
          nomand: {
            ...prev.nomand,
            tuning: {
              ...(prev.nomand?.tuning ?? DEFAULT_NOMAND_BACKGROUND_TUNING),
              ...patch,
            },
          },
        }))
      },
      onResetTuning: () => {
        setBackground(prev => ({
          ...prev,
          nomand: { ...prev.nomand, tuning: { ...DEFAULT_NOMAND_BACKGROUND_TUNING } },
        }))
      },
    },
    url: {
      onAdd: (item: UrlBackgroundItem) => {
        setBackground(prev => ({
          ...prev,
          url: {
            ...prev.url,
            items: [...(prev.url?.items ?? []), item],
            selectedId: item.id,
          },
        }))
      },
      onUpdate: (id, patch) => {
        setBackground(prev => ({
          ...prev,
          url: {
            ...prev.url,
            items: (prev.url?.items ?? []).map(entry => (
              entry.id === id ? { ...entry, ...patch } : entry
            )),
          },
        }))
      },
      onDelete: (id) => {
        setBackground(prev => ({
          ...prev,
          url: {
            ...prev.url,
            items: (prev.url?.items ?? []).filter(entry => entry.id !== id),
            selectedId: prev.url?.selectedId === id ? null : prev.url?.selectedId,
          },
        }))
      },
      onSelect: (id) => {
        setBackground(prev => ({
          ...prev,
          url: { ...prev.url, selectedId: id },
        }))
      },
    },
  }), [])

  const settingsPanelExtra = useMemo(() => ({
    classicTuning: tunings.classic,
    onClassicTuningChange: (patch: Partial<NonNullable<VisualizerTuningBundle['classic']>>) => patchTuning('classic', patch),
    partitaTuning: tunings.partita,
    onPartitaTuningChange: (patch: Partial<NonNullable<VisualizerTuningBundle['partita']>>) => patchTuning('partita', patch),
    fumeTuning: tunings.fume,
    onFumeTuningChange: (patch: Partial<NonNullable<VisualizerTuningBundle['fume']>>) => patchTuning('fume', patch),
    claddaghTuning: tunings.claddagh,
    onCladdaghTuningChange: (patch: Partial<NonNullable<VisualizerTuningBundle['claddagh']>>) => patchTuning('claddagh', patch),
    cappellaTuning: tunings.cappella,
    onCappellaTuningChange: (patch: Partial<NonNullable<VisualizerTuningBundle['cappella']>>) => patchTuning('cappella', patch),
    tiltTuning: tunings.tilt,
    onTiltTuningChange: (patch: Partial<NonNullable<VisualizerTuningBundle['tilt']>>) => patchTuning('tilt', patch),
    dioramaTuning: tunings.diorama,
    onDioramaTuningChange: (patch: Partial<NonNullable<VisualizerTuningBundle['diorama']>>) => patchTuning('diorama', patch),
    monetTuning: tunings.monet,
    onMonetTuningChange: (patch: Partial<NonNullable<VisualizerTuningBundle['monet']>>) => patchTuning('monet', patch),
    pendoloTuning: tunings.pendolo,
    onPendoloTuningChange: (patch: Partial<NonNullable<VisualizerTuningBundle['pendolo']>>) => patchTuning('pendolo', patch),
    sonnetTuning: tunings.sonnet,
    onSonnetTuningChange: (patch: Partial<NonNullable<VisualizerTuningBundle['sonnet']>>) => patchTuning('sonnet', patch),
    temperaTuning: tunings.tempera,
    onTemperaTuningChange: (patch: Partial<NonNullable<VisualizerTuningBundle['tempera']>>) => patchTuning('tempera', patch),
  }), [tunings])

  return (
    <LyricStagePlayerChrome
      theme={theme}
      onThemeChange={onThemeChange}
      isDaylight={effectiveIsDaylight}
      onDaylightChange={setIsDaylight}
      visualizerMode={mode}
      onVisualizerModeChange={setMode}
      backgroundConfig={background}
      backgroundActions={backgroundActions}
      currentTime={currentTime}
      duration={Math.max(duration, 0.01)}
      paused={paused}
      onTogglePlay={onTogglePlay}
      onSeek={onSeek}
      coverUrl={coverUrl ?? null}
      songTitle={songTitle}
      songArtist={songArtist}
      tracks={tracks}
      currentTrackId={currentTrackId}
      onSelectTrack={recordingBusy ? undefined : onSelectTrack}
      onPrevTrack={recordingBusy ? undefined : onPrevTrack}
      onNextTrack={recordingBusy ? undefined : onNextTrack}
      onExitLyricStage={onExitLyricStage}
      chromeHidden={chromeHidden}
      recordSupported={recordSupported}
      recordStatus={recordStatus}
      showRecordHud={showRecordHud}
      onStageSurfaceReady={onStageSurfaceReady}
      onStartPvRecord={onStartPvRecord}
      onCancelPvRecord={onCancelPvRecord}
      settingsPanelExtra={settingsPanelExtra}
      appearanceOverrides={{
        visualizerBadges: JIZURA_MODE_BADGES,
        ...(mode === 'mod:musey:jizura' ? {
          visualizerQuickControl: <JizuraPalettePicker isDaylight={effectiveIsDaylight} />,
          backgroundRowOverride: <JizuraVersionRow isDaylight={effectiveIsDaylight} />,
          hideDaylightControls: true,
        } : {}),
      }}
      appearancePanelExtra={mode === 'mod:musey:jizura' ? <JizuraSettingsPanel accentColor={theme.primaryColor} /> : undefined}
      panelFooter={panelFooter}
    >
      <LyricStage
        mode={mode}
        lyrics={lyrics}
        theme={theme}
        subtitleTheme={theme}
        currentTime={currentTime}
        audioPower={audioPower}
        audioBands={audioBands}
        paused={paused}
        isDaylight={effectiveIsDaylight}
        songTitle={songTitle}
        songArtist={songArtist}
        coverUrl={coverUrl ?? null}
        background={background}
        visualizerTunings={tunings}
        classicTuning={tunings.classic}
        partitaTuning={tunings.partita}
        fumeTuning={tunings.fume}
        claddaghTuning={tunings.claddagh}
        cappellaTuning={tunings.cappella}
        tiltTuning={tunings.tilt}
        dioramaTuning={tunings.diorama}
        monetTuning={tunings.monet}
        pendoloTuning={tunings.pendolo}
        sonnetTuning={tunings.sonnet}
        temperaTuning={tunings.tempera}
        showText
      />
    </LyricStagePlayerChrome>
  )
}

const LyricStageIsland: React.FC<LyricStageIslandProps> = (props) => (
  <I18nextProvider i18n={i18n}>
    <LyricStageIslandInner {...props} />
  </I18nextProvider>
)

export default LyricStageIsland
